import { initWebSocket } from './socket';
import { getToken } from '@/utils/auth';
import store from '@/store';

class GlobalWebSocketManager {
  constructor() {
    this.webSocketConnection = null;
    this.isConnected = false;
    this.messageHandlers = new Map();
    this.reconnectTimer = null;
    this.maxReconnectAttempts = 5;
    this.reconnectAttempts = 0;
    this.reconnectInterval = 3000;
    this.initTimer = null;
    this.initDelay = 1000; // 延迟初始化时间
  }

  /**
   * 初始化WebSocket连接
   */
  init() {
    // 如果已经连接，跳过初始化
    if (this.isConnected || this.webSocketConnection) {
      console.log('WebSocket连接已存在，跳过初始化');
      return;
    }

    const token = getToken();
    if (!token) {
      console.log('未找到认证token，跳过WebSocket初始化');
      return;
    }

    // 检查是否正在初始化中
    if (this._isInitializing) {
      console.log('WebSocket正在初始化中，跳过重复调用');
      return;
    }

    this._isInitializing = true;

    // 延迟初始化，确保用户已完全登录
    if (this.initTimer) {
      clearTimeout(this.initTimer);
    }

    this.initTimer = setTimeout(() => {
      this.createConnection();
    }, this.initDelay);
  }

  /**
   * 创建WebSocket连接
   */
  async createConnection() {
    try {
      console.log('开始创建全局WebSocket连接...');
      
      this.webSocketConnection = await initWebSocket({
        onOpen: () => {
          console.log('全局WebSocket连接已建立');
          this.isConnected = true;
          this.reconnectAttempts = 0;
          this.clearReconnectTimer();
          this._isInitializing = false; // 重置初始化状态
          
          // 连接建立后，通知所有注册的消息处理器
          this.notifyConnectionEstablished();
        },
        onClose: () => {
          console.log('全局WebSocket连接已关闭');
          this.isConnected = false;
          this.webSocketConnection = null;
          this._isInitializing = false; // 重置初始化状态
          this.scheduleReconnect();
        },
        onError: (error) => {
          console.error('全局WebSocket连接错误:', error);
          this.isConnected = false;
          this._isInitializing = false; // 重置初始化状态
          this.handleError(error);
        }
      });

      // 添加消息监听器
      if (this.webSocketConnection && this.webSocketConnection.addMessageListener) {
        this.webSocketConnection.addMessageListener((data) => {
          this.handleMessage(data);
        });
      }

    } catch (error) {
      console.error('创建全局WebSocket连接失败:', error);
      this._isInitializing = false; // 重置初始化状态
      this.handleError(error);
    }
  }

  /**
   * 处理接收到的消息
   */
  handleMessage(data) {
    try {
      let processedData;
      
      // 智能识别消息类型
      if (typeof data === 'string') {
        // 字符串类型的消息
        if (data === 'ping' || data === 'pong') {
          // 心跳消息，不需要JSON解析
          console.log('收到WebSocket心跳消息:', data);
          return; // 心跳消息不传递给处理器
        }
        
        try {
          // 尝试JSON解析
          processedData = JSON.parse(data);
        } catch (parseError) {
          // 解析失败，可能是纯文本消息
          console.log('收到WebSocket文本消息:', data);
          processedData = { type: 'text', content: data };
        }
      } else if (typeof data === 'object' && data !== null) {
        // 已经是对象，直接使用
        processedData = data;
      } else {
        // 其他类型，转换为字符串
        processedData = { type: 'unknown', content: String(data) };
      }

      console.log('全局WebSocket收到消息:', processedData);

      // 通知所有注册的消息处理器
      this.messageHandlers.forEach((handler, key) => {
        try {
          handler(processedData);
        } catch (error) {
          console.error(`WebSocket消息处理器 ${key} 执行失败:`, error);
        }
      });

    } catch (error) {
      console.error('处理WebSocket消息失败:', error);
    }
  }

  /**
   * 处理连接错误
   */
  handleError(error) {
    this.isConnected = false;
    console.error('WebSocket连接错误，尝试重连:', error);

    if (this.reconnectAttempts < this.maxReconnectAttempts) {
      this.scheduleReconnect();
    } else {
      console.error('WebSocket重连次数已达上限，停止重连');
    }
  }

  /**
   * 安排重连
   */
  scheduleReconnect() {
    this.clearReconnectTimer();

    this.reconnectTimer = setTimeout(() => {
      this.reconnectAttempts++;
      console.log(`WebSocket重连尝试 ${this.reconnectAttempts}/${this.maxReconnectAttempts}`);

      if (this.webSocketConnection) {
        try {
          this.webSocketConnection.close();
        } catch (error) {
          console.error('关闭WebSocket连接失败:', error);
        }
        this.webSocketConnection = null;
      }

      this.createConnection();
    }, this.reconnectInterval);
  }

  /**
   * 清除重连定时器
   */
  clearReconnectTimer() {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  /**
   * 清除初始化定时器
   */
  clearInitTimer() {
    if (this.initTimer) {
      clearTimeout(this.initTimer);
      this.initTimer = null;
    }
  }

  /**
   * 通知连接已建立
   */
  notifyConnectionEstablished() {
    // 可以在这里添加连接建立后的逻辑
    console.log('WebSocket连接已建立，可以开始接收消息');
  }

  /**
   * 注册消息处理器
   * @param {string} key 处理器标识
   * @param {function} handler 消息处理函数
   */
  registerMessageHandler(key, handler) {
    this.messageHandlers.set(key, handler);
    console.log(`注册WebSocket消息处理器: ${key}`);
  }

  /**
   * 注销消息处理器
   * @param {string} key 处理器标识
   */
  unregisterMessageHandler(key) {
    this.messageHandlers.delete(key);
    console.log(`注销WebSocket消息处理器: ${key}`);
  }

  /**
   * 获取连接状态
   */
  getConnectionState() {
    return this.isConnected;
  }

  /**
   * 获取WebSocket连接实例
   */
  getConnection() {
    return this.webSocketConnection;
  }

  /**
   * 断开连接
   */
  disconnect() {
    this.clearReconnectTimer();
    this.clearInitTimer();

    if (this.webSocketConnection) {
      try {
        this.webSocketConnection.close();
      } catch (error) {
        console.error('关闭WebSocket连接失败:', error);
      }
      this.webSocketConnection = null;
    }

    this.isConnected = false;
    this.messageHandlers.clear();
    console.log('全局WebSocket连接已断开');
  }

  /**
   * 重新连接
   */
  reconnect() {
    this.reconnectAttempts = 0;
    this.disconnect();
    this.init();
  }

  /**
   * 安全初始化（用于路由守卫，不阻塞路由导航）
   */
  safeInit() {
    // 如果已经连接，跳过初始化
    if (this.isConnected || this.webSocketConnection) {
      return;
    }

    const token = getToken();
    if (!token) {
      return;
    }

    // 使用setTimeout确保不阻塞路由导航
    setTimeout(() => {
      this.init();
    }, 100);
  }

  /**
   * 发送消息
   * @param {any} data 要发送的数据
   */
  sendMessage(data) {
    if (this.webSocketConnection && this.isConnected) {
      try {
        this.webSocketConnection.send(data);
        return true;
      } catch (error) {
        console.error('发送WebSocket消息失败:', error);
        return false;
      }
    } else {
      console.warn('WebSocket未连接，无法发送消息');
      return false;
    }
  }
}

// 创建全局单例实例
const globalWebSocketManager = new GlobalWebSocketManager();

export default globalWebSocketManager;
